import type { Middleware } from 'openapi-fetch';

/**
 * W3C tracecontext, console end (architecture §B.11 / decision T-13).
 *
 * The chain the certification's trace-continuity test asserts starts here: console →
 * gateway → gRPC → pgx. If the console omits `traceparent`, the gateway starts a fresh trace and
 * the request the user actually made is no longer the thing you can look up in Jaeger — so the
 * header is injected on EVERY request, not on a sampled subset. Sampling is the collector's job.
 */

const VERSION = '00';
/** `01` = sampled. The console does not sample; the collector decides what to keep. */
const SAMPLED = '01';

/** A traceparent the app already holds — an OTel web SDK, or a page-level root span. */
export type TraceparentProvider = () => string | undefined;

const TRACEPARENT_PATTERN = /^[\da-f]{2}-[\da-f]{32}-[\da-f]{16}-[\da-f]{2}$/;

export function isValidTraceparent(value: string): boolean {
  if (!TRACEPARENT_PATTERN.test(value)) return false;
  const [, traceId, spanId] = value.split('-');
  // All-zero ids are invalid per the spec and would produce an unqueryable trace.
  return traceId !== '0'.repeat(32) && spanId !== '0'.repeat(16);
}

function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return Array.from(buffer, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function generateTraceparent(): string {
  return `${VERSION}-${randomHex(16)}-${randomHex(8)}-${SAMPLED}`;
}

export function createTraceparentMiddleware(provider?: TraceparentProvider): Middleware {
  return {
    onRequest({ request }) {
      if (request.headers.has('traceparent')) return undefined;
      const supplied = provider?.();
      request.headers.set(
        'traceparent',
        supplied !== undefined && isValidTraceparent(supplied) ? supplied : generateTraceparent(),
      );
      return request;
    },
  };
}
